import { createAccessControl } from "better-auth/plugins/access";

/**
 * Resource + action statement for Tarboot RBAC.
 * `as const` is required for TypeScript inference.
 *
 * The `user` and `session` resources include the built-in better-auth admin
 * actions so that platform_admin can use the admin plugin endpoints
 * (listUsers, banUser, impersonateUser, etc.).
 */
export const statement = {
  // Built-in admin resources — extended with Tarboot-specific actions
  user: [
    // better-auth admin built-ins
    "create", "list", "set-role", "ban", "impersonate", "delete", "set-password",
    // Tarboot custom
    "read", "update", "deactivate",
  ],
  session: ["list", "revoke", "delete"],
  // Tarboot-specific resources
  event: ["create", "read", "update", "delete", "archive", "cancel", "share"],
  vendor: [
    "read",
    "contact",
    "assign",
    "rate",
    "approve",
    "reject",
    "suspend",
    "reactivate",
    "score_override",
  ],
  document: ["upload", "read", "delete", "request", "export"],
  budget: ["read", "update", "approve_quote", "reject_quote", "export"],
  organization: ["read", "update", "manage_users", "manage_structure"],
  opportunity: ["read", "accept", "decline", "submit_quote", "revise_quote"],
  report: ["create", "revoke"],
  scoring: ["read", "override"],
  platform: ["access_admin", "view_analytics", "manage_billing"],
} as const;

export const ac = createAccessControl(statement);

/**
 * producer — event producers / institutional clients.
 * Full event lifecycle, vendor discovery, budget, documents, shared reports.
 */
export const producer = ac.newRole({
  event: ["create", "read", "update", "delete", "archive", "cancel", "share"],
  vendor: ["read", "contact", "assign", "rate"],
  document: ["upload", "read", "delete", "request", "export"],
  budget: ["read", "update", "approve_quote", "reject_quote", "export"],
  organization: ["read"],
  opportunity: ["read"],
  report: ["create", "revoke"],
});

/**
 * org_admin — producer who is also an organization administrator.
 * All producer permissions + org structure/user management within their org.
 */
export const org_admin = ac.newRole({
  event: ["create", "read", "update", "delete", "archive", "cancel", "share"],
  vendor: ["read", "contact", "assign", "rate"],
  document: ["upload", "read", "delete", "request", "export"],
  budget: ["read", "update", "approve_quote", "reject_quote", "export"],
  organization: ["read", "update", "manage_users", "manage_structure"],
  user: ["read", "update", "deactivate"],
  opportunity: ["read"],
  report: ["create", "revoke"],
});

/**
 * vendor — service providers.
 * Opportunity inbox, compliance documents, own profile, performance/scoring.
 */
export const vendor = ac.newRole({
  opportunity: ["read", "accept", "decline", "submit_quote", "revise_quote"],
  document: ["upload", "read"],
  scoring: ["read"],
  vendor: ["read"],
});

/**
 * platform_admin — Tarboot internal administrators.
 * Provisioned directly (not via public registration). Full access.
 * Includes all built-in admin plugin permissions so admin API endpoints work.
 */
export const platform_admin = ac.newRole({
  // Built-in admin plugin resources
  user: [
    "create", "list", "set-role", "ban", "impersonate", "delete", "set-password",
    "read", "update", "deactivate",
  ],
  session: ["list", "revoke", "delete"],
  // Tarboot resources
  event: ["create", "read", "update", "delete", "archive", "cancel", "share"],
  vendor: [
    "read",
    "contact",
    "assign",
    "rate",
    "approve",
    "reject",
    "suspend",
    "reactivate",
    "score_override",
  ],
  document: ["upload", "read", "delete", "request", "export"],
  budget: ["read", "update", "approve_quote", "reject_quote", "export"],
  organization: ["read", "update", "manage_users", "manage_structure"],
  opportunity: ["read", "accept", "decline", "submit_quote", "revise_quote"],
  report: ["create", "revoke"],
  scoring: ["read", "override"],
  platform: ["access_admin", "view_analytics", "manage_billing"],
});
